/**
 * Permisos RBAC: se cargan desde public/permissions.json en runtime.
 * Así puedes añadir/quitar permisos editando solo el JSON sin recompilar.
 * Clave de permiso -> array de role IDs que pueden acceder. null = todos.
 */
import { ROLE_IDS } from './constants';

const {
  ADMIN,
  MANAGER,
  RENTAL_MANAGER,
  TOUR_MANAGER,
  BASE_MANAGER,
  FINANCIAL,
  AGENT,
  RESERVATIONS,
  SUPER_ADMIN,
} = ROLE_IDS;

/** Mapa nombre de rol -> ID (para resolver el JSON) */
const ROLE_NAME_TO_ID = {
  ADMIN,
  MANAGER,
  RENTAL_MANAGER,
  TOUR_MANAGER,
  BASE_MANAGER,
  FINANCIAL,
  AGENT,
  RESERVATIONS,
  SUPER_ADMIN,
};

/** Fallback por si el JSON no está disponible (misma estructura que antes) */
const DEFAULT_PERMISSIONS = null; // se quita porque ahora se carga el JSON en runtime y no se usa el fallback permitiendo agregar permisos en runtime sin recompilar
/*const DEFAULT_PERMISSIONS = {
  dashboard: null,
  payments: [ADMIN, FINANCIAL, SUPER_ADMIN],
  clients: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, BASE_MANAGER, FINANCIAL, SUPER_ADMIN],
  agencies: [ADMIN, MANAGER, RENTAL_MANAGER, SUPER_ADMIN],
  clientsMerge: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, FINANCIAL, SUPER_ADMIN],
  tour_due_list: [ADMIN, MANAGER, TOUR_MANAGER, FINANCIAL, SUPER_ADMIN],
  tour_management: [ADMIN, MANAGER, TOUR_MANAGER, FINANCIAL, RESERVATIONS, SUPER_ADMIN],
  tours: [ADMIN, MANAGER, TOUR_MANAGER, BASE_MANAGER, FINANCIAL, AGENT, RESERVATIONS, SUPER_ADMIN],
  pending_rentals: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, BASE_MANAGER, FINANCIAL, SUPER_ADMIN],
  rentals_due: [ADMIN, MANAGER, RENTAL_MANAGER, FINANCIAL, SUPER_ADMIN],
  whiteboard: [ADMIN, MANAGER, RENTAL_MANAGER, BASE_MANAGER, FINANCIAL, AGENT, SUPER_ADMIN],
  moto_schedule: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, BASE_MANAGER, FINANCIAL, SUPER_ADMIN],
  gps_schedule: [ADMIN, MANAGER, SUPER_ADMIN],
  vans_schedule: [ADMIN, MANAGER, SUPER_ADMIN],
  weekly_rentals: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, BASE_MANAGER, FINANCIAL, AGENT, SUPER_ADMIN],
  transfers: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, BASE_MANAGER, FINANCIAL, AGENT, SUPER_ADMIN],
  current_fleet: [ADMIN, MANAGER, BASE_MANAGER, FINANCIAL, SUPER_ADMIN],
  fleet_allocation: [ADMIN, MANAGER, SUPER_ADMIN],
  tour_prices: [ADMIN, MANAGER, TOUR_MANAGER, FINANCIAL, SUPER_ADMIN],
  tour_list: [ADMIN, MANAGER, TOUR_MANAGER, FINANCIAL, SUPER_ADMIN],
  rentals: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, FINANCIAL, SUPER_ADMIN],
  vehicles: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, BASE_MANAGER, FINANCIAL, AGENT, SUPER_ADMIN],
  gps_devices: [ADMIN, BASE_MANAGER, SUPER_ADMIN],
  holds: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, BASE_MANAGER, FINANCIAL, SUPER_ADMIN],
  unentered_km_rentals: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, BASE_MANAGER, FINANCIAL, AGENT, SUPER_ADMIN],
  extra_km_rentals: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, BASE_MANAGER, FINANCIAL, SUPER_ADMIN],
  vehicle_damages: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, FINANCIAL, SUPER_ADMIN],
  vehicle_damages_refund: [ADMIN, MANAGER, FINANCIAL],
  rentals_edit: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, SUPER_ADMIN],
  black_lists: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, BASE_MANAGER, FINANCIAL, SUPER_ADMIN],
  reports: [SUPER_ADMIN],
  seats: [ADMIN, SUPER_ADMIN],
  tankbags: [ADMIN, SUPER_ADMIN],
  gloves: [ADMIN, SUPER_ADMIN],
  helmets: [ADMIN, SUPER_ADMIN],
  jackets: [ADMIN, SUPER_ADMIN],
  users: [ADMIN, SUPER_ADMIN],
  dashboard_rentals_card: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, BASE_MANAGER, FINANCIAL, AGENT, SUPER_ADMIN],
  dashboard_fleet_card: [ADMIN, MANAGER, RENTAL_MANAGER, TOUR_MANAGER, BASE_MANAGER, FINANCIAL, AGENT, SUPER_ADMIN],
  dashboard_tours_card: [ADMIN, MANAGER, TOUR_MANAGER, BASE_MANAGER, FINANCIAL, AGENT, RESERVATIONS, SUPER_ADMIN],
  dashboard_reports_card: [MANAGER, RENTAL_MANAGER, BASE_MANAGER, FINANCIAL, SUPER_ADMIN],
  reportsYearSales: [SUPER_ADMIN],
  reportsEmails: [SUPER_ADMIN, RENTAL_MANAGER, MANAGER, BASE_MANAGER],
  reportsTours: [SUPER_ADMIN],
  reportsRentals: [SUPER_ADMIN],
  reportsSales: [SUPER_ADMIN],
  reportsRentalsByRange: [SUPER_ADMIN],
  reportToursCharges: [SUPER_ADMIN, FINANCIAL],
  reportFutureIncome: [SUPER_ADMIN],
  maintenances_due: [ADMIN, MANAGER, BASE_MANAGER, FINANCIAL, AGENT, SUPER_ADMIN],
  tour_guides: [ADMIN, SUPER_ADMIN],
  tour_bikes: [ADMIN, SUPER_ADMIN],
  tour_gps_devices: [ADMIN, SUPER_ADMIN],
  tour_vans: [ADMIN, SUPER_ADMIN],
  guides: [ADMIN, SUPER_ADMIN],
  countries: [ADMIN, SUPER_ADMIN],
  locations: [ADMIN, SUPER_ADMIN],
  holidays: [ADMIN, FINANCIAL, SUPER_ADMIN],
  tour_names: [ADMIN, MANAGER, TOUR_MANAGER, FINANCIAL, SUPER_ADMIN],
  vehicle_models: [ADMIN, SUPER_ADMIN],
  maintenance_item_types: [ADMIN, SUPER_ADMIN],
  maintenance_items: [ADMIN, SUPER_ADMIN],
};*/

/** Cache de permisos resueltos (clave -> array de role IDs). Se rellena al cargar el JSON. */
let permissionsCache = null;

/**
 * Resuelve un objeto de permisos en formato JSON (roles por nombre) al formato interno (roles por ID).
 * @param {Object} json - { permissionKey: null | string[] }
 * @returns {Object} - { permissionKey: null | number[] }
 */
function resolvePermissionsJson(json) {
  const out = {};
  for (const [key, value] of Object.entries(json)) {
    if (value === null) {
      out[key] = null;
    } else if (Array.isArray(value)) {
      out[key] = value
        .map((name) => ROLE_NAME_TO_ID[name])
        .filter((id) => id !== undefined);
    } else {
      out[key] = null;
    }
  }
  return out;
}

/**
 * Carga public/permissions.json y actualiza la caché.
 * Llamar al arranque de la app (ej. en App.js). Si falla, se sigue usando DEFAULT_PERMISSIONS.
 * @returns {Promise<Object>} - el mapa de permisos ya resuelto (clave -> array de role IDs o null)
 */
export function loadPermissions() {
  const url = `${process.env.PUBLIC_URL || ''}/permissions.json`;
  return fetch(url, { cache: 'no-store' })
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error('permissions.json not found'))))
    .then((json) => {
      permissionsCache = resolvePermissionsJson(json);
      return permissionsCache;
    })
    .catch(() => {
      permissionsCache = DEFAULT_PERMISSIONS;
      return permissionsCache;
    });
}

/**
 * Devuelve el mapa de permisos actual (cache o fallback).
 * Formato: { permissionKey: null | number[] } (null = todos, array = role IDs permitidos).
 */
export function getPermissions() {
  return permissionsCache != null ? permissionsCache : DEFAULT_PERMISSIONS;
}

/** Compatibilidad: PERMISSIONS sigue siendo el objeto actual (lectura vía getPermissions) */
export const PERMISSIONS = new Proxy(
  {},
  {
    get(_, key) {
      const p = getPermissions();
      return p != null ? p[key] : undefined;
    },
    has(_, key) {
      const p = getPermissions();
      return p != null && key in p;
    },
    ownKeys() {
      const p = getPermissions();
      return p != null ? Reflect.ownKeys(p) : [];
    },
    getOwnPropertyDescriptor(_, key) {
      const p = getPermissions();
      return { enumerable: true, configurable: true, value: p != null ? p[key] : undefined };
    },
  }
);



// WEBPACK FOOTER //
// ./src/rbac/permissions.js